import React, { useState, useEffect } from "react";
import { AnimatePresence } from 'framer-motion';
import { AuthProvider } from './contexts/AuthContext';
import { BookingDraftProvider } from './contexts/BookingDraftContext';
import { BrandProvider } from './contexts/BrandContext';
import { UserLocationProvider } from './contexts/UserLocationContext';
import Routes from "./Routes";
import { supabase } from './lib/supabase';
import SplashScreen from './components/SplashScreen';

// SAFE MODE: PWA - تسجيل SW من أول تحميل والتقاط حدث التثبيت من أي صفحة (حتى لا يُفوت في لوحة المصور)
function usePwaInstallCapture() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
    const registerSw = () => {
      navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
    };
    window.addEventListener('load', registerSw);
    registerSw();
    const onInstallPrompt = (e) => {
      e.preventDefault();
      window.__PWA_INSTALL_PROMPT = e;
    };
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    return () => {
      window.removeEventListener('load', registerSw);
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
    };
  }, []);
}

// SAFE MODE: Google Analytics 4 — حقن script تلقائياً من app_settings
function useGoogleAnalytics() {
  useEffect(() => {
    // لا تُشغّل في بيئة التطوير
    if (import.meta.env.DEV) return;

    let injected = false;

    const inject = (measurementId) => {
      if (!measurementId || injected) return;
      injected = true;

      // تجنّب حقن مكرر
      if (document.querySelector(`script[data-ga-id="${measurementId}"]`)) return;

      // 1. تهيئة dataLayer مبكراً
      window.dataLayer = window.dataLayer || [];
      window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        // إخفاء آخر أوكتت من IP (ممارسة الخصوصية الجيدة)
        anonymize_ip: true,
        // لا إرسال صفحة تلقائي عند التحميل الأول — نُرسله بعد تهيئة الـ config
        send_page_view: true,
      });

      // 2. تحميل مكتبة gtag.js
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      script.setAttribute('data-ga-id', measurementId);
      document.head.appendChild(script);
    };

    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'google_analytics_id')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) inject(data.value.trim());
      })
      .catch(() => {});
  }, []);
}

// SAFE MODE: Snapchat Pixel — حقن script تلقائياً من app_settings
function useSnapchatPixel() {
  useEffect(() => {
    if (import.meta.env.DEV) return;

    let injected = false;

    const inject = (pixelId) => {
      if (!pixelId || injected) return;
      injected = true;

      if (document.querySelector(`script[data-snap-pixel-id="${pixelId}"]`)) return;

      // تهيئة snaptr
      window.snaptr = window.snaptr || function () {
        (window.snaptr.q = window.snaptr.q || []).push(arguments);
      };

      // تحميل مكتبة Snapchat Pixel
      const script = document.createElement('script');
      script.src = 'https://sc-static.net/scevent.min.js';
      script.async = true;
      script.setAttribute('data-snap-pixel-id', pixelId);
      document.head.appendChild(script);

      script.onload = () => {
        window.snaptr('init', pixelId);
        window.snaptr('track', 'PAGE_VIEW');
      };
    };

    supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'snapchat_pixel_id')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) inject(data.value.trim());
      })
      .catch(() => {});
  }, []);
}

function App() {
  usePwaInstallCapture();
  useGoogleAnalytics();
  useSnapchatPixel();

  const [showSplash, setShowSplash] = useState(() => {
    if (typeof sessionStorage === 'undefined') return false;
    // عرض السبلاش فقط على التطبيق الأصلي (Capacitor) أو PWA مثبّت — ليس على متصفح الويب العادي
    const isNative = typeof window !== 'undefined' &&
      (window.Capacitor?.isNativePlatform?.() === true);
    const isPwa = typeof window !== 'undefined' && (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.matchMedia?.('(display-mode: fullscreen)').matches ||
      window.navigator?.standalone === true
    );
    if (!isNative && !isPwa) return false;
    if (sessionStorage.getItem('splash_shown')) return false;
    sessionStorage.setItem('splash_shown', '1');
    return true;
  });

  return (
    <BrandProvider>
      <AuthProvider>
        <BookingDraftProvider>
          <UserLocationProvider>
            <AnimatePresence>
              {showSplash && (
                <SplashScreen key="splash" onDone={() => setShowSplash(false)} />
              )}
            </AnimatePresence>
            <Routes />
          </UserLocationProvider>
        </BookingDraftProvider>
      </AuthProvider>
    </BrandProvider>
  );
}

export default App;
